import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Wrench, Loader2, Plus, Trash2, ShieldCheck, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { MASTER_DOC_TYPES, hasInventoryItems } from "@/lib/documentTypes";
import { AssignEmbarqueModal } from "@/components/AssignEmbarqueModal";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string | null;
  archivoUrl: string | null;
}

interface ItemRow {
  id: string;
  sku: string;
  descripcion: string;
  cantidad: string;
  precio_unitario: string;
  unidad_medida: string;
  peso_bruto_kg: string;
}

const MONEDAS = ["USD", "EUR", "COP", "MXN", "CNY", "BRL"] as const;
const TIPO_DOC_OPTIONS = MASTER_DOC_TYPES.filter((t) => t !== "Otro");

const newRow = (): ItemRow => ({
  id: crypto.randomUUID(),
  sku: "",
  descripcion: "",
  cantidad: "",
  precio_unitario: "",
  unidad_medida: "UN",
  peso_bruto_kg: "",
});

export function ManualIngestModal({ open, onOpenChange, jobId, archivoUrl }: Props) {
  const { user, orgId } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { secureUrl, loading: urlLoading } = useSignedUrl(open ? archivoUrl : null, "documentos_kargo");

  // Cabecera
  const [tipoDocumento, setTipoDocumento] = useState<string>("Factura Internacional");
  const [noDocumento, setNoDocumento] = useState("");
  const [fecha, setFecha] = useState("");
  const [emisor, setEmisor] = useState("");
  const [receptor, setReceptor] = useState("");
  const [moneda, setMoneda] = useState<string>("USD");
  const [costoFlete, setCostoFlete] = useState("");
  const [seguro, setSeguro] = useState("");
  const [impuestos, setImpuestos] = useState("");
  const [origen, setOrigen] = useState("");
  const [destino, setDestino] = useState("");
  const [incoterm, setIncoterm] = useState("");
  const [notas, setNotas] = useState("");
  const [pesoBruto, setPesoBruto] = useState("");
  const [items, setItems] = useState<ItemRow[]>([newRow()]);
  const [saving, setSaving] = useState(false);

  // Post-save: assign-embarque modal
  const [assignDocId, setAssignDocId] = useState<string | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);

  const showInventory = useMemo(() => hasInventoryItems(tipoDocumento), [tipoDocumento]);

  const totalCalculado = useMemo(() => {
    if (!showInventory) return 0;
    return items.reduce((acc, it) => {
      const c = parseFloat(it.cantidad) || 0;
      const p = parseFloat(it.precio_unitario) || 0;
      return acc + c * p;
    }, 0);
  }, [items, showInventory]);

  const updateItem = (id: string, key: keyof ItemRow, value: string) => {
    setItems((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
  };

  const removeItem = (id: string) => {
    setItems((prev) => (prev.length === 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  const reset = () => {
    setTipoDocumento("Factura Internacional");
    setNoDocumento("");
    setFecha("");
    setEmisor("");
    setReceptor("");
    setMoneda("USD");
    setCostoFlete("");
    setSeguro("");
    setImpuestos("");
    setOrigen("");
    setDestino("");
    setIncoterm("");
    setNotas("");
    setPesoBruto("");
    setItems([newRow()]);
  };

  const handleSave = async () => {
    if (!user?.id || !orgId || !jobId) return;
    if (!noDocumento.trim()) {
      toast({ title: "Falta No. Documento", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      // 1) INSERT registro_documentos con fuente_ingesta = KILL_SWITCH_MANUAL
      //    El trigger trg_log_document_usage descontará el crédito automáticamente.
      const headerInsert: any = {
        user_id: user.id,
        org_id: orgId,
        archivo_url: archivoUrl,
        tipo_documento: tipoDocumento || null,
        no_documento: noDocumento.trim(),
        fecha: fecha || null,
        emisor: emisor || null,
        receptor: receptor || null,
        moneda: moneda || null,
        costo_flete: parseFloat(costoFlete) || 0,
        seguro: parseFloat(seguro) || 0,
        impuestos: parseFloat(impuestos) || 0,
        origen: origen || null,
        destino: destino || null,
        incoterm: incoterm || null,
        notas_tecnicas: notas || null,
        peso_bruto_kg: pesoBruto ? parseFloat(pesoBruto) : null,
        total_factura: showInventory ? totalCalculado : 0,
        estado: "Pendiente",
        fuente_ingesta: "KILL_SWITCH_MANUAL",
      };

      const { data: docInserted, error: docErr } = await supabase
        .from("registro_documentos")
        .insert(headerInsert)
        .select("id")
        .single();
      if (docErr) throw docErr;
      const documentId = (docInserted as any).id as string;

      // 2) Items de inventario (si aplica)
      if (showInventory) {
        const validItems = items.filter((r) => r.descripcion.trim() || r.sku.trim());
        if (validItems.length > 0) {
          const itemsInsert = validItems.map((r, idx) => {
            const cantidad = parseFloat(r.cantidad) || 0;
            const precio = parseFloat(r.precio_unitario) || 0;
            return {
              org_id: orgId,
              documento_id: documentId,
              no_documento: noDocumento.trim(),
              item_no: idx + 1,
              sku: r.sku || null,
              descripcion: r.descripcion || null,
              cantidad,
              unidad_medida: r.unidad_medida || "UN",
              precio_unitario: precio,
              sub_total: cantidad * precio,
              peso_bruto_kg: parseFloat(r.peso_bruto_kg) || 0,
              aplica_tlc: false,
              beneficio_aduanero: "GENERAL",
            };
          });
          const { error: itemsErr } = await supabase.from("base_inventario").insert(itemsInsert as any);
          if (itemsErr) throw itemsErr;
        }
      }

      // 3) Audit log: rastro inmutable de la ingesta manual (encadena SHA-256)
      await supabase.from("audit_logs").insert({
        document_id: documentId,
        field_name: "ingesta_manual",
        old_value: jobId,
        new_value: "KILL_SWITCH_MANUAL",
      } as any);

      // 4) Cerrar el job: marcarlo completed + recovered_via=kill_switch
      await supabase
        .from("document_jobs" as any)
        .update({
          status: "completed",
          document_id: documentId,
          error_message: null,
          job_metadata: { recovered_via: "kill_switch", recovered_at: new Date().toISOString() },
        } as any)
        .eq("id", jobId);

      toast({ title: "✅ Documento digitado y registrado", description: "Se descontó 1 crédito (regla de negocio)." });

      // Invalidate caches
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["all-documents"] });
      queryClient.invalidateQueries({ queryKey: ["failed-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["monthly-credits"] });
      queryClient.invalidateQueries({ queryKey: ["org-quota"] });

      // Cerrar modal y disparar asignación a embarque
      reset();
      onOpenChange(false);
      setAssignDocId(documentId);
      setAssignOpen(true);
    } catch (err: any) {
      console.error("Kill Switch save error:", err);
      toast({ title: "Error al guardar", description: err.message ?? "Error desconocido", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[92vh] p-0 flex flex-col gap-0">
          <DialogHeader className="px-4 py-3 border-b border-border shrink-0">
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Wrench className="h-4 w-4 text-amber-500" />
              Kill Switch — Digitación Manual
              <span className="text-xs text-muted-foreground font-normal ml-2">
                (Consume 1 crédito según regla de negocio)
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0">
            <ResizablePanelGroup direction="horizontal" className="h-full">
              {/* PDF Viewer */}
              <ResizablePanel defaultSize={50} minSize={30}>
                <div className="h-full bg-muted/20 p-2">
                  {urlLoading ? (
                    <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
                      <ShieldCheck className="h-10 w-10 animate-pulse text-primary" />
                      <span className="text-sm">Autenticando documento…</span>
                      <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                  ) : !secureUrl ? (
                    <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                      No se pudo cargar el archivo original.
                    </div>
                  ) : (
                    <iframe src={secureUrl} title="Documento original" className="w-full h-full rounded border-0 bg-white" />
                  )}
                </div>
              </ResizablePanel>

              <ResizableHandle withHandle />

              {/* Form */}
              <ResizablePanel defaultSize={50} minSize={35}>
                <div className="h-full overflow-y-auto p-4 space-y-4">
                  <Alert className="border-amber-500/30 bg-amber-500/10">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <AlertTitle className="text-sm">Modo Kill Switch activo</AlertTitle>
                    <AlertDescription className="text-xs">
                      Estás digitando un documento manualmente porque la IA falló. Este registro quedará marcado como{" "}
                      <code className="bg-muted px-1 py-0.5 rounded">KILL_SWITCH_MANUAL</code> y trazado en audit_logs.
                    </AlertDescription>
                  </Alert>

                  {/* Cabecera */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Tipo de Documento *</Label>
                      <Select value={tipoDocumento} onValueChange={setTipoDocumento}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TIPO_DOC_OPTIONS.map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">No. Documento *</Label>
                      <Input className="h-9" value={noDocumento} onChange={(e) => setNoDocumento(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Fecha</Label>
                      <Input className="h-9" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Moneda</Label>
                      <Select value={moneda} onValueChange={setMoneda}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {MONEDAS.map((m) => (
                            <SelectItem key={m} value={m}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Emisor</Label>
                      <Input className="h-9" value={emisor} onChange={(e) => setEmisor(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Receptor</Label>
                      <Input className="h-9" value={receptor} onChange={(e) => setReceptor(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Origen</Label>
                      <Input className="h-9" value={origen} onChange={(e) => setOrigen(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Destino</Label>
                      <Input className="h-9" value={destino} onChange={(e) => setDestino(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Incoterm</Label>
                      <Input className="h-9" value={incoterm} onChange={(e) => setIncoterm(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Peso Bruto (KG)</Label>
                      <Input className="h-9 tabular-nums" type="number" value={pesoBruto} onChange={(e) => setPesoBruto(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Costo Flete</Label>
                      <Input className="h-9 tabular-nums" type="number" value={costoFlete} onChange={(e) => setCostoFlete(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Seguro</Label>
                      <Input className="h-9 tabular-nums" type="number" value={seguro} onChange={(e) => setSeguro(e.target.value)} />
                    </div>
                    <div className="space-y-1.5 col-span-2">
                      <Label className="text-xs">Impuestos</Label>
                      <Input className="h-9 tabular-nums" type="number" value={impuestos} onChange={(e) => setImpuestos(e.target.value)} />
                    </div>
                    <div className="space-y-1.5 col-span-2">
                      <Label className="text-xs">Notas Técnicas</Label>
                      <Input className="h-9" value={notas} onChange={(e) => setNotas(e.target.value)} />
                    </div>
                  </div>

                  {/* Items */}
                  {showInventory && (
                    <div className="space-y-2 pt-2 border-t border-border">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-semibold">Ítems de Inventario</Label>
                        <Button size="sm" variant="outline" onClick={() => setItems((p) => [...p, newRow()])} className="h-7 gap-1">
                          <Plus className="h-3 w-3" /> Agregar ítem
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {items.map((row, idx) => (
                          <div key={row.id} className="grid grid-cols-[auto_1fr_2fr_80px_80px_100px_80px_auto] gap-1.5 items-center text-xs">
                            <span className="text-muted-foreground tabular-nums w-5 text-center">{idx + 1}</span>
                            <Input className="h-8 text-xs" placeholder="SKU" value={row.sku} onChange={(e) => updateItem(row.id, "sku", e.target.value)} />
                            <Input className="h-8 text-xs" placeholder="Descripción" value={row.descripcion} onChange={(e) => updateItem(row.id, "descripcion", e.target.value)} />
                            <Input className="h-8 text-xs tabular-nums" placeholder="Cant." type="number" value={row.cantidad} onChange={(e) => updateItem(row.id, "cantidad", e.target.value)} />
                            <Input className="h-8 text-xs" placeholder="UM" value={row.unidad_medida} onChange={(e) => updateItem(row.id, "unidad_medida", e.target.value)} />
                            <Input className="h-8 text-xs tabular-nums" placeholder="P.Unit" type="number" value={row.precio_unitario} onChange={(e) => updateItem(row.id, "precio_unitario", e.target.value)} />
                            <Input className="h-8 text-xs tabular-nums" placeholder="Peso" type="number" value={row.peso_bruto_kg} onChange={(e) => updateItem(row.id, "peso_bruto_kg", e.target.value)} />
                            <Button size="icon" variant="ghost" onClick={() => removeItem(row.id)} disabled={items.length === 1} className="h-7 w-7">
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-end pt-1 text-xs">
                        <span className="text-muted-foreground mr-2">Total calculado:</span>
                        <span className="font-semibold tabular-nums">{totalCalculado.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {moneda}</span>
                      </div>
                    </div>
                  )}
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>

          <div className="px-4 py-3 border-t border-border shrink-0 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving || !noDocumento.trim()} className="gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Guardar y asignar embarque
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AssignEmbarqueModal open={assignOpen} onOpenChange={setAssignOpen} documentId={assignDocId} />
    </>
  );
}
